import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Calendar, Shield, BarChart2, History, Trophy, ClipboardList, Footprints, Shirt, Award, Medal, Award as RecordIcon, LogOut, LogIn, Search } from 'lucide-react';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Configurar axios para enviar el token en cada petición (excepto login)
axios.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    const isAuthLogin = url.includes('/auth/session') || url.includes('/auth/login');
    if (!isAuthLogin) {
      const token = localStorage.getItem('token');
      if (token) {
        // Ensure headers object exists and set Bearer token
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const CAMPOS_ESTADISTICAS = [
  { key: 'partidosJugados', label: 'PJ' },
  { key: 'golesTotales', label: 'Goles' },
  { key: 'golesPieDerecho', label: 'Pie Der.' },
  { key: 'golesPieIzquierdo', label: 'Pie Izq.' },
  { key: 'golesCabeza', label: 'Cabeza' },
  { key: 'golesBalonParado', label: 'Balón Parado' },
  { key: 'golesNoEspecificado', label: 'No Espec.' },
  { key: 'golesPropiaMeta', label: 'Propia Meta' },
  { key: 'asistencias', label: 'Asistencias' },
  { key: 'tarjetasAmarillas', label: 'Amarillas' },
  { key: 'tarjetasAzules', label: 'Azules' },
  { key: 'tarjetasRojas', label: 'Rojas' },
];

const ordenarTemporadasPorAnio = (lista) =>
  [...lista].sort((a, b) => b.año.localeCompare(a.año));

const mostrarValor = (valor) =>
  valor !== null && valor !== undefined && valor !== '' ? valor : '-';

const calcularExpulsiones = (est) => {
  const azules = est.tarjetasAzules || 0;
  const rojas = est.tarjetasRojas || 0;
  return azules > 0 ? azules : rojas;
};

const calcularGolesPorPartido = (goles, partidosJugados) => {
  if (!partidosJugados || partidosJugados <= 0) return '-';
  return (goles / partidosJugados).toFixed(2);
};

const getResultadoColor = (resultado) => {
  if (!resultado) return '';
  const res = resultado.toUpperCase();
  if (res === 'G' || res === 'GANADO' || res === 'VICTORIA') return 'text-green-600 font-bold bg-green-50';
  if (res === 'P' || res === 'PERDIDO' || res === 'DERROTA') return 'text-red-600 font-bold bg-red-50';
  if (res === 'E' || res === 'EMPATADO' || res === 'EMPATE') return 'text-yellow-600 font-bold bg-yellow-50';
  return '';
};

const ordenarEstadisticasTemporada = (estadisticas) =>
  [...estadisticas].sort((a, b) => {
    const golesA = a.golesTotales || 0;
    const golesB = b.golesTotales || 0;
    if (golesB !== golesA) return golesB - golesA;

    const asistenciasA = a.asistencias || 0;
    const asistenciasB = b.asistencias || 0;
    if (asistenciasB !== asistenciasA) return asistenciasB - asistenciasA;

    const partidosA = a.partidosJugados || 0;
    const partidosB = b.partidosJugados || 0;
    const golesPorPartidoA = partidosA > 0 ? golesA / partidosA : 0;
    const golesPorPartidoB = partidosB > 0 ? golesB / partidosB : 0;
    if (golesPorPartidoB !== golesPorPartidoA) return golesPorPartidoB - golesPorPartidoA;

    return partidosB - partidosA;
  });

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { temporadaId, partidoId, jugadorId } = useParams();
  const [activeTab, setActiveTab] = useState('allMnls');
  const [loading, setLoading] = useState(false);
  const [jugadores, setJugadores] = useState([]);
  const [temporadas, setTemporadas] = useState([]);
  const [jugadoresRecientes, setJugadoresRecientes] = useState([]);
  const [filtroJugadores, setFiltroJugadores] = useState('');
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState(null);
  const [partidosTemporada, setPartidosTemporada] = useState([]);
  const [partidoSeleccionado, setPartidoSeleccionado] = useState(null);
  const [partidoForm, setPartidoForm] = useState({
    temporadaId: '',
    fecha: '',
    lugar: '',
    rival: '',
    resultado: '',
    marcador: '',
    noPresentadoRival: false,
    noPresentadoNosotros: false,
    golesPrimeraParte: '',
    golesSegundaParte: '',
    golesRivalPrimeraParte: '',
    golesRivalSegundaParte: '',
    numeroJornada: '',
    idaVuelta: '',
    descripcion: '',
    jugadoresParticipantes: [],
    goles: []
  });
  const [jugadoresSeleccionados, setJugadoresSeleccionados] = useState([]);
  const [golForm, setGolForm] = useState({
    jugadorId: '',
    minutosJugados: '',
    tipoDeGol: '',
    resultadoTrasGol: '',
    parte: '',
    asistencias: '',
    tarjetasAmarillas: '',
    tarjetasRojas: '',
    tarjetasAzules: ''
  });
  const [jugadorEstadisticasId, setJugadorEstadisticasId] = useState('');
  const [estadisticasJugador, setEstadisticasJugador] = useState([]);
  const [loadingEstadisticas, setLoadingEstadisticas] = useState(false);
  const [recordsJugador, setRecordsJugador] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [temporadaVistaId, setTemporadaVistaId] = useState('');
  const [temporadaVista, setTemporadaVista] = useState(null);
  const [estadisticasTemporadaVista, setEstadisticasTemporadaVista] = useState([]);
  const [loadingEstadisticasTemporada, setLoadingEstadisticasTemporada] = useState(false);
  const [resumenCalculado, setResumenCalculado] = useState(null);
  const [loadingResumenCalculado, setLoadingResumenCalculado] = useState(false);
  const [estadisticasCalculadas, setEstadisticasCalculadas] = useState([]);
  const [loadingEstadisticasCalculadas, setLoadingEstadisticasCalculadas] = useState(false);
  const [estadisticasAgregadas, setEstadisticasAgregadas] = useState([]);
  const [loadingEstadisticasAgregadas, setLoadingEstadisticasAgregadas] = useState(false);
  const [registrarTemporadaId, setRegistrarTemporadaId] = useState('');
  const [registrarPartidos, setRegistrarPartidos] = useState([]);
  const [partidosBusqueda, setPartidosBusqueda] = useState([]);
  const [rivalBusqueda, setRivalBusqueda] = useState('');
  const [loadingBusquedaPartidos, setLoadingBusquedaPartidos] = useState(false);
  const [errorBusquedaPartidos, setErrorBusquedaPartidos] = useState('');
  const [registrarPartidoId, setRegistrarPartidoId] = useState('');
  const [registrarNuevoPartido, setRegistrarNuevoPartido] = useState(false);
  const [registrarPartidoDetalle, setRegistrarPartidoDetalle] = useState(null);
  const [registrarForm, setRegistrarForm] = useState({
    jugadoresParticipantes: [],
    goles: []
  });
  const [registrarJugadoresSeleccionados, setRegistrarJugadoresSeleccionados] = useState([]);
  const [registrarGolForm, setRegistrarGolForm] = useState({
    jugadorId: '',
    minutosJugados: '',
    tipoDeGol: 'NO_ESPECIFICADO',
    resultadoTrasGolLocal: '',
    resultadoTrasGolVisitante: '',
    parte: '1',
    asistencias: '',
    tarjetasAmarillas: '',
    tarjetasRojas: '',
    tarjetasAzules: ''
  });
  const [loadingRegistrar, setLoadingRegistrar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [recordsEquipo, setRecordsEquipo] = useState(null);
  const [loadingRecordsEquipo, setLoadingRecordsEquipo] = useState(false);
  const [recordsIndividuales, setRecordsIndividuales] = useState(null);
  const [loadingRecordsIndividuales, setLoadingRecordsIndividuales] = useState(false);
  const registrarInitPath = useRef('');
  const initRegistrarPartidoRef = useRef(null);
  const busquedaInitPath = useRef('');

  const fetchJugadores = async () => {
    try {
      const response = await axios.get(`${API_URL}/jugadores`);
      setJugadores(response.data);
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching jugadores:', error);
    }
  };

  const fetchTemporadas = async () => {
    try {
      const response = await axios.get(`${API_URL}/temporadas`);
      const sorted = ordenarTemporadasPorAnio(response.data);
      setTemporadas(sorted);
      return sorted;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching temporadas:', error);
      return [];
    }
  };

  const fetchEstadisticasByTemporada = async (temporadaId) => {
    if (!temporadaId) {
      setEstadisticasTemporadaVista([]);
      return;
    }

    setLoadingEstadisticasTemporada(true);
    try {
      const response = await axios.get(`${API_URL}/temporadas/${temporadaId}/estadisticas-jugadores`);
      setEstadisticasTemporadaVista(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching estadisticas temporada:', error);
      setEstadisticasTemporadaVista([]);
    } finally {
      setLoadingEstadisticasTemporada(false);
    }
  };

  const fetchResumenCalculado = async (temporadaId) => {
    if (!temporadaId) {
      setResumenCalculado(null);
      return;
    }

    setLoadingResumenCalculado(true);
    try {
      const response = await axios.get(`${API_URL}/temporadas/${temporadaId}/resumen-calculado`);
      setResumenCalculado(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching resumen calculado:', error);
      setResumenCalculado(null);
    } finally {
      setLoadingResumenCalculado(false);
    }
  };

  const fetchEstadisticasCalculadas = async (temporadaId) => {
    if (!temporadaId) {
      setEstadisticasCalculadas([]);
      return;
    }

    setLoadingEstadisticasCalculadas(true);
    try {
      const response = await axios.get(`${API_URL}/temporadas/${temporadaId}/estadisticas-jugadores-calculadas`);
      setEstadisticasCalculadas(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching estadisticas calculadas:', error);
      setEstadisticasCalculadas([]);
    } finally {
      setLoadingEstadisticasCalculadas(false);
    }
  };

  const initTemporadaVista = async () => {
    const sorted = await fetchTemporadas();
    if (sorted.length > 0) {
      setTemporadaVistaId(String(sorted[0].id));
      setTemporadaVista(sorted[0]);
      fetchEstadisticasByTemporada(sorted[0].id);
      fetchResumenCalculado(sorted[0].id);
      fetchEstadisticasCalculadas(sorted[0].id);
    } else {
      setTemporadaVistaId('');
      setTemporadaVista(null);
      setEstadisticasTemporadaVista([]);
      setResumenCalculado(null);
      setEstadisticasCalculadas([]);
    }
  };

  const handleTemporadaVistaChange = (temporadaId) => {
    const temporada = temporadas.find(t => t.id === parseInt(temporadaId, 10));
    setTemporadaVistaId(temporadaId);
    setTemporadaVista(temporada || null);
    fetchEstadisticasByTemporada(temporadaId);
    fetchResumenCalculado(temporadaId);
    fetchEstadisticasCalculadas(temporadaId);
  };

  const fetchJugadoresRecientes = async () => {
    try {
      const response = await axios.get(`${API_URL}/jugadores/recientes`);
      if (process.env.NODE_ENV !== 'production') console.log('Jugadores recientes:', response.data);
      setJugadoresRecientes(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching jugadores recientes:', error);
    }
  };

  const fetchRecordsEquipo = async () => {
    setLoadingRecordsEquipo(true);
    try {
      const response = await axios.get(`${API_URL}/records/equipo`);
      if (process.env.NODE_ENV !== 'production') console.log('Records equipo:', response.data);
      setRecordsEquipo(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching records equipo:', error);
      setRecordsEquipo(null);
    } finally {
      setLoadingRecordsEquipo(false);
    }
  };

  const fetchRecordsIndividuales = async () => {
    setLoadingRecordsIndividuales(true);
    try {
      const response = await axios.get(`${API_URL}/records/individuales`);
      setRecordsIndividuales(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching records individuales:', error);
      setRecordsIndividuales(null);
    } finally {
      setLoadingRecordsIndividuales(false);
    }
  };

  const fetchEstadisticasJugador = async (jugadorId) => {
    if (!jugadorId) {
      setEstadisticasJugador([]);
      return;
    }

    setLoadingEstadisticas(true);
    try {
      const response = await axios.get(`${API_URL}/jugadores/${jugadorId}/estadisticas-temporada`);
      setEstadisticasJugador(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching estadisticas jugador:', error);
      setEstadisticasJugador([]);
    } finally {
      setLoadingEstadisticas(false);
    }
  };

  const fetchRecordsJugador = async (jugadorId) => {
    if (!jugadorId) {
      setRecordsJugador(null);
      return;
    }

    setLoadingRecords(true);
    try {
      const response = await axios.get(`${API_URL}/jugadores/${jugadorId}/records`);
      setRecordsJugador(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching records jugador:', error);
      setRecordsJugador(null);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleJugadorEstadisticasChange = (e) => {
    const jugadorId = e.target.value;
    setJugadorEstadisticasId(jugadorId);
    fetchEstadisticasJugador(jugadorId);
    fetchRecordsJugador(jugadorId);
  };

  const fetchEstadisticasAgregadas = async () => {
    setLoadingEstadisticasAgregadas(true);
    try {
      const response = await axios.get(`${API_URL}/jugadores/estadisticas-agregadas`);
      setEstadisticasAgregadas(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching estadisticas agregadas:', error);
      setEstadisticasAgregadas([]);
    } finally {
      setLoadingEstadisticasAgregadas(false);
    }
  };

  useEffect(() => {
    fetchEstadisticasAgregadas();
  }, []);

  const irADetalleJugador = async (jugadorId) => {
    navigate(`/jugadores/${jugadorId}`);
    setJugadorEstadisticasId(String(jugadorId));
    if (jugadores.length === 0) {
      await fetchJugadores();
    }
    fetchEstadisticasJugador(jugadorId);
    fetchRecordsJugador(jugadorId);
  };

  const calcularTotalesEstadisticas = (estadisticas) => {
    return CAMPOS_ESTADISTICAS.reduce((totales, campo) => {
      totales[campo.key] = estadisticas.reduce(
        (suma, est) => suma + (est[campo.key] || 0),
        0
      );
      return totales;
    }, {});
  };

  const fetchPartidosByTemporada = async (temporadaId) => {
    try {
      const response = await axios.get(`${API_URL}/partidos/temporada/${temporadaId}`);
      const partidos = response.data || [];
      // Ordenar por numeroJornada (asc). Los partidos sin numeroJornada quedan al final.
      partidos.sort((a, b) => {
        const aJ = a.numeroJornada != null ? parseInt(a.numeroJornada, 10) : Number.POSITIVE_INFINITY;
        const bJ = b.numeroJornada != null ? parseInt(b.numeroJornada, 10) : Number.POSITIVE_INFINITY;
        return aJ - bJ;
      });
      setPartidosTemporada(partidos);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching partidos by temporada:', error);
    }
  };

  const cargarPartidosBusqueda = async () => {
    setLoadingBusquedaPartidos(true);
    setErrorBusquedaPartidos('');
    try {
      const temporadasResponse = await axios.get(`${API_URL}/temporadas`);
      const temporadasDisponibles = ordenarTemporadasPorAnio(temporadasResponse.data || []);
      setTemporadas(temporadasDisponibles);
      const partidosPorTemporada = await Promise.all(
        temporadasDisponibles.map(async temporada => {
          const response = await axios.get(`${API_URL}/partidos/temporada/${temporada.id}`);
          return (response.data || []).map(partido => ({
            ...partido,
            temporadaId: temporada.id,
            temporadaAño: temporada.año
          }));
        })
      );
      setPartidosBusqueda(partidosPorTemporada.flat());
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error cargando partidos para búsqueda:', error);
      setPartidosBusqueda([]);
      setErrorBusquedaPartidos('No se pudieron cargar los partidos. Inténtalo de nuevo.');
    } finally {
      setLoadingBusquedaPartidos(false);
    }
  };

  const fetchRegistrarPartidos = async (temporadaId) => {
    if (!temporadaId) {
      setRegistrarPartidos([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/partidos/temporada/${temporadaId}`);
      const partidos = response.data || [];
      // Ordenar por numeroJornada (asc). Los partidos sin numeroJornada quedan al final.
      partidos.sort((a, b) => {
        const aJ = a.numeroJornada != null ? parseInt(a.numeroJornada, 10) : Number.POSITIVE_INFINITY;
        const bJ = b.numeroJornada != null ? parseInt(b.numeroJornada, 10) : Number.POSITIVE_INFINITY;
        return aJ - bJ;
      });
      setRegistrarPartidos(partidos);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching partidos para registrar:', error);
      setRegistrarPartidos([]);
    }
  };

  const fetchRegistrarPartidoDetalle = async (partidoId) => {
    if (!partidoId) {
      setRegistrarPartidoDetalle(null);
      setRegistrarForm({ jugadoresParticipantes: [], goles: [] });
      setRegistrarJugadoresSeleccionados([]);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/partidos/${partidoId}/detalle`);
      const detalle = response.data;
      setRegistrarPartidoDetalle(detalle);
      setRegistrarForm({
        jugadoresParticipantes: detalle.jugadoresParticipantes || [],
        goles: detalle.goles || []
      });

      const idsParticipantes = (detalle.jugadoresParticipantes || []).map(jp => jp.jugadorId);
      const goleadoresIds = (detalle.goles || []).map(g => g.jugadorId);
      const todosIds = [...new Set([...idsParticipantes, ...goleadoresIds])];
      
      const seleccionados = todosIds.map(id => {
        let jugador = jugadores.find(j => j.id === id);
        
        if (jugador) {
          return { id: jugador.id, nombre: jugador.nombre, posicion: jugador.posicion };
        }
        
        const participante = (detalle.jugadoresParticipantes || []).find(jp => jp.jugadorId === id);
        if (participante && participante.jugador) {
          return { id: participante.jugador.id, nombre: participante.jugador.nombre, posicion: participante.jugador.posicion };
        }
        
        const goleador = (detalle.goles || []).find(g => g.jugadorId === id);
        if (goleador) {
          const jugadorInfo = (detalle.jugadoresParticipantes || []).find(jp => jp.jugadorId === id)?.jugador;
          if (jugadorInfo) {
            return { id: id, nombre: jugadorInfo.nombre, posicion: jugadorInfo.posicion };
          }
        }
        
        return null;
      }).filter(j => j !== null);
      
      setRegistrarJugadoresSeleccionados(seleccionados);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching detalle partido:', error);
      setRegistrarPartidoDetalle(null);
      setRegistrarForm({ jugadoresParticipantes: [], goles: [] });
      setRegistrarJugadoresSeleccionados([]);
    }
  };

  const initRegistrarPartidoTab = async () => {
    setRegistrarNuevoPartido(false);
    const sorted = await fetchTemporadas();
    await fetchJugadores();
    if (sorted.length > 0) {
      const primeraTemporadaId = String(sorted[0].id);
      setRegistrarTemporadaId(primeraTemporadaId);
      setRegistrarPartidoId('');
      setRegistrarPartidoDetalle(null);
      setRegistrarForm({ jugadoresParticipantes: [], goles: [] });
      setRegistrarJugadoresSeleccionados([]);
      await fetchRegistrarPartidos(sorted[0].id);
    } else {
      setRegistrarTemporadaId('');
      setRegistrarPartidos([]);
    }
  };
  initRegistrarPartidoRef.current = initRegistrarPartidoTab;

  const handleRegistrarTemporadaChange = async (temporadaId) => {
    setRegistrarTemporadaId(temporadaId);
    setRegistrarPartidoId('');
    setRegistrarPartidoDetalle(null);
    setRegistrarForm({ jugadoresParticipantes: [], goles: [] });
    setRegistrarJugadoresSeleccionados([]);
    await fetchRegistrarPartidos(temporadaId);
  };

  const handleRegistrarPartidoChange = async (partidoId) => {
    setRegistrarPartidoId(partidoId);
    setRegistrarGolForm({
      jugadorId: '',
      minutosJugados: '',
      tipoDeGol: 'NO_ESPECIFICADO',
     resultadoTrasGolLocal: '1',
     resultadoTrasGolVisitante: '0',
     resultadoTrasGol: '1-0',
     parte: '1',
     asistencias: '',
     tarjetasAmarillas: '',
     tarjetasRojas: '',
     tarjetasAzules: ''
   });
   await fetchRegistrarPartidoDetalle(partidoId);
  };

  const iniciarNuevoPartido = () => {
    setRegistrarNuevoPartido(true);
    setRegistrarPartidoId('');
    setRegistrarPartidoDetalle(null);
    setRegistrarForm({ jugadoresParticipantes: [], goles: [] });
    setRegistrarJugadoresSeleccionados([]);
    setPartidoForm({
      temporadaId: registrarTemporadaId,
      fecha: '',
      lugar: '',
      rival: '',
      resultado: '',
      marcador: '',
      noPresentadoRival: false,
      noPresentadoNosotros: false,
      golesPrimeraParte: '',
      golesSegundaParte: '',
      golesRivalPrimeraParte: '',
      golesRivalSegundaParte: '',
      numeroJornada: '',
      idaVuelta: '',
      descripcion: '',
      jugadoresParticipantes: [],
      goles: []
    });
    setGolForm({
      jugadorId: '',
      minutosJugados: '',
      tipoDeGol: '',
      resultadoTrasGol: '',
      parte: '',
      asistencias: '',
      tarjetasAmarillas: '',
      tarjetasRojas: '',
      tarjetasAzules: ''
    });
    setRegistrarGolForm({
      jugadorId: '',
      minutosJugados: '',
      tipoDeGol: 'NO_ESPECIFICADO',
      resultadoTrasGolLocal: '1',
      resultadoTrasGolVisitante: '0',
      resultadoTrasGol: '1-0',
      parte: '1',
      asistencias: '',
      tarjetasAmarillas: '',
      tarjetasRojas: '',
      tarjetasAzules: ''
    });
  };

  const handleRegistrarGolFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'resultadoTrasGolLocal' || name === 'resultadoTrasGolVisitante') {
      const updatedForm = { ...registrarGolForm, [name]: value };
      const local = name === 'resultadoTrasGolLocal' ? value : registrarGolForm.resultadoTrasGolLocal;
      const visitante = name === 'resultadoTrasGolVisitante' ? value : registrarGolForm.resultadoTrasGolVisitante;
      
      if (local && visitante) {
        updatedForm.resultadoTrasGol = `${local}-${visitante}`;
      } else if (local) {
        updatedForm.resultadoTrasGol = local;
      } else if (visitante) {
        updatedForm.resultadoTrasGol = visitante;
      }
      
      setRegistrarGolForm(updatedForm);
    } else if (name === 'jugadorId') {
      // Para jugadorId, actualizamos directamente sin generar un nuevo objeto innecesariamente
      setRegistrarGolForm({ ...registrarGolForm, [name]: value });
    } else {
      setRegistrarGolForm({ ...registrarGolForm, [name]: value });
    }
  };

  const handleRegistrarJugadoresSeleccionadosChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(option => parseInt(option.value, 10));
    const jugadoresData = selectedOptions.map(id => {
      const jugador = jugadores.find(j => j.id === id);
      if (jugador) {
        return { id: jugador.id, nombre: jugador.nombre, posicion: jugador.posicion };
      }
      return null;
    }).filter(j => j !== null);
    setRegistrarJugadoresSeleccionados(jugadoresData);
  };

  const agregarEstadisticasRegistrar = () => {
    if (!registrarGolForm.jugadorId) return;

    const jugadorId = parseInt(registrarGolForm.jugadorId, 10);
    if (!registrarJugadoresSeleccionados.find(j => j.id === jugadorId)) return;

    const estadisticasExistentes = registrarForm.jugadoresParticipantes.find(j => j.jugadorId === jugadorId);

    if (estadisticasExistentes) {
      setRegistrarForm({
        ...registrarForm,
        jugadoresParticipantes: registrarForm.jugadoresParticipantes.map(j =>
          j.jugadorId === jugadorId
            ? {
                ...j,
                minutosJugados: registrarGolForm.minutosJugados || j.minutosJugados,
                asistencias: registrarGolForm.asistencias || j.asistencias,
                tarjetasAmarillas: registrarGolForm.tarjetasAmarillas || j.tarjetasAmarillas,
                tarjetasRojas: registrarGolForm.tarjetasRojas || j.tarjetasRojas,
                tarjetasAzules: registrarGolForm.tarjetasAzules || j.tarjetasAzules
              }
            : j
        )
      });
    } else {
      setRegistrarForm({
        ...registrarForm,
        jugadoresParticipantes: [
          ...registrarForm.jugadoresParticipantes,
          {
            jugadorId,
            minutosJugados: registrarGolForm.minutosJugados || 0,
            asistencias: registrarGolForm.asistencias || 0,
            tarjetasAmarillas: registrarGolForm.tarjetasAmarillas || 0,
            tarjetasRojas: registrarGolForm.tarjetasRojas || 0,
            tarjetasAzules: registrarGolForm.tarjetasAzules || 0
          }
        ]
      });
    }

    setRegistrarGolForm({
      ...registrarGolForm,
      minutosJugados: '',
      asistencias: '',
      tarjetasAmarillas: '',
      tarjetasRojas: '',
      tarjetasAzules: ''
    });
  };

  const agregarGolRegistrar = () => {
    if (!registrarGolForm.jugadorId) return;

    const jugadorId = parseInt(registrarGolForm.jugadorId, 10);
    if (!registrarJugadoresSeleccionados.find(j => j.id === jugadorId)) return;

    setRegistrarForm({
      ...registrarForm,
      goles: [
        ...registrarForm.goles,
        {
          jugadorId,
          tipoDeGol: registrarGolForm.tipoDeGol || 'NO_ESPECIFICADO',
          resultadoTrasGol: registrarGolForm.resultadoTrasGol,
          parte: registrarGolForm.parte
        }
      ]
    });

    // Incrementar el siguiente valor para "Nuestro equipo" (máximo 8)
    const nextLocal = Math.min(parseInt(registrarGolForm.resultadoTrasGolLocal || 0, 10) + 1, 8);
    
    setRegistrarGolForm({
      ...registrarGolForm,
      tipoDeGol: 'NO_ESPECIFICADO',
      resultadoTrasGolLocal: String(nextLocal),
      resultadoTrasGol: `${nextLocal}-${registrarGolForm.resultadoTrasGolVisitante}`
    });
  };

  const eliminarJugadorRegistrar = (index) => {
    const nuevosJugadores = registrarForm.jugadoresParticipantes.filter((_, i) => i !== index);
    setRegistrarForm({ ...registrarForm, jugadoresParticipantes: nuevosJugadores });
  };

  const eliminarGolRegistrar = (index) => {
    const nuevosGoles = registrarForm.goles.filter((_, i) => i !== index);
    setRegistrarForm({ ...registrarForm, goles: nuevosGoles });
  };

  const handleSubmitParticipacion = async (e) => {
    e.preventDefault();
    if (!registrarPartidoId) return;

    setLoadingRegistrar(true);
    try {
      const participantesMap = new Map();
      registrarForm.jugadoresParticipantes.forEach(j => participantesMap.set(j.jugadorId, j));
      registrarJugadoresSeleccionados.forEach(j => {
        if (!participantesMap.has(j.id)) {
          participantesMap.set(j.id, {
            jugadorId: j.id,
            minutosJugados: 0,
            asistencias: 0,
            tarjetasAmarillas: 0,
            tarjetasRojas: 0,
            tarjetasAzules: 0
          });
        }
      });
      registrarForm.goles.forEach(g => {
        if (!participantesMap.has(g.jugadorId)) {
          participantesMap.set(g.jugadorId, {
            jugadorId: g.jugadorId,
            minutosJugados: 0,
            asistencias: 0,
            tarjetasAmarillas: 0,
            tarjetasRojas: 0,
            tarjetasAzules: 0
          });
        }
      });

      const payload = {
        jugadoresParticipantes: Array.from(participantesMap.values()).map(j => ({
          jugadorId: j.jugadorId,
          minutosJugados: j.minutosJugados ? parseInt(j.minutosJugados, 10) : 0,
          asistencias: j.asistencias ? parseInt(j.asistencias, 10) : 0,
          tarjetasAmarillas: j.tarjetasAmarillas ? parseInt(j.tarjetasAmarillas, 10) : 0,
          tarjetasRojas: j.tarjetasRojas ? parseInt(j.tarjetasRojas, 10) : 0,
          tarjetasAzules: j.tarjetasAzules ? parseInt(j.tarjetasAzules, 10) : 0
        })),
        goles: registrarForm.goles.map(g => ({
          jugadorId: g.jugadorId,
          tipoDeGol: g.tipoDeGol,
          resultadoTrasGol: g.resultadoTrasGol,
          parte: g.parte || null
        }))
      };

      await axios.put(`${API_URL}/partidos/${registrarPartidoId}/participacion`, payload);
      
      // Mostrar mensaje que desaparezca automáticamente
      setSuccessMessage('Participación guardada correctamente');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Cargar siguiente partido automáticamente
      const currentIndex = registrarPartidos.findIndex(p => p.id === parseInt(registrarPartidoId, 10));
      if (currentIndex !== -1 && currentIndex < registrarPartidos.length - 1) {
        const nextPartidoId = registrarPartidos[currentIndex + 1].id;
        await handleRegistrarPartidoChange(String(nextPartidoId));
      } else {
        // Si no hay siguiente, recargar el actual
        await fetchRegistrarPartidoDetalle(registrarPartidoId);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error guardando participación:', error);
      setErrorMessage('Error al guardar la participación');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoadingRegistrar(false);
    }
  };

  const handleTemporadaClick = (temporada) => {
    navigate(`/historico/${temporada.id}`);
    setTemporadaSeleccionada(temporada);
    fetchPartidosByTemporada(temporada.id);
    setPartidoSeleccionado(null);
  };

  const handlePartidoClick = async (partido) => {
    const temporadaPartidoId = partido.temporadaId || temporadaSeleccionada?.id;
    if (temporadaPartidoId && !partidoId) {
      navigate(`/historico/${temporadaPartidoId}/${partido.id}`);
    }
    // Ensure we have a local copy of jugadores (fetch if necessary) because setState isn't immediate
    let availablePlayers = jugadores;
    if (!availablePlayers || availablePlayers.length === 0) {
      availablePlayers = await fetchJugadores() || [];
    }

    try {
      const response = await axios.get(`${API_URL}/partidos/${partido.id}/detalle`);
      const detalle = response.data;

      // Build participantes enriquecidos con datos del jugador y conteo de goles por tipo
      const participantesMap = new Map();

      // Inicializar con los jugadoresParticipantes recibidos
      (detalle.jugadoresParticipantes || []).forEach(jp => {
        const jugadorObj = availablePlayers.find(j => String(j.id) === String(jp.jugadorId)) || { id: jp.jugadorId, nombre: 'Desconocido' };
        participantesMap.set(jp.jugadorId, {
          id: jp.jugadorId,
          jugador: jugadorObj,
          minutosJugados: jp.minutosJugados || 0,
          asistencias: jp.asistencias || 0,
          tarjetasAmarillas: jp.tarjetasAmarillas || 0,
          tarjetasRojas: jp.tarjetasRojas || 0,
          tarjetasAzules: jp.tarjetasAzules || 0,
          golesTotales: 0,
          golesPieDerecho: 0,
          golesPieIzquierdo: 0,
          golesCabeza: 0,
          golesBalonParado: 0,
          golesNoEspecificado: 0,
          golesPropiaMeta: 0,
          parte: jp.parte || '-'
        });
      });

      // Contar goles y asignar a participantes (si aparece un goleador que no estaba en participantes, lo añadimos)
      (detalle.goles || []).forEach(g => {
        const pid = g.jugadorId;
        let p = participantesMap.get(pid);
        if (!p) {
          const jugadorObj = availablePlayers.find(j => String(j.id) === String(pid)) || { id: pid, nombre: 'Desconocido' };
          p = {
            id: pid,
            jugador: jugadorObj,
            minutosJugados: 0,
            asistencias: 0,
            tarjetasAmarillas: 0,
            tarjetasRojas: 0,
            tarjetasAzules: 0,
            golesTotales: 0,
            golesPieDerecho: 0,
            golesPieIzquierdo: 0,
            golesCabeza: 0,
            golesBalonParado: 0,
            golesNoEspecificado: 0,
            golesPropiaMeta: 0,
            parte: g.parte || '-'
          };
          participantesMap.set(pid, p);
        }

        p.golesTotales = (p.golesTotales || 0) + 1;
        const tipo = (g.tipoDeGol || '').toUpperCase();
        if (tipo === 'PIE_DERECHO') p.golesPieDerecho += 1;
        else if (tipo === 'PIE_IZQUIERDO') p.golesPieIzquierdo += 1;
        else if (tipo === 'CABEZA') p.golesCabeza += 1;
        else if (tipo === 'BALON_PARADO') p.golesBalonParado += 1;
        else if (tipo === 'NO_ESPECIFICADO') p.golesNoEspecificado += 1;
        else if (tipo === 'PROPIA_META') p.golesPropiaMeta += 1;

        if ((!p.parte || p.parte === '-') && g.parte) p.parte = g.parte;
      });

      const participantes = Array.from(participantesMap.values());

      // Crear objeto partidoSeleccionado con detalle y participaciones enriquecidas
      const partidoDetalle = {
        id: detalle.id,
        fecha: detalle.fecha,
        lugar: detalle.lugar,
        rival: detalle.rival,
        resultado: detalle.resultado,
        marcador: detalle.marcador,
        golesPrimeraParte: detalle.golesPrimeraParte,
        golesSegundaParte: detalle.golesSegundaParte,
        golesRivalPrimeraParte: detalle.golesRivalPrimeraParte,
        golesRivalSegundaParte: detalle.golesRivalSegundaParte,
        numeroJornada: detalle.numeroJornada,
        idaVuelta: detalle.idaVuelta,
        descripcion: detalle.descripcion,
        participacionesJugadores: participantes,
        goles: detalle.goles || []
      };

      setPartidoSeleccionado(partidoDetalle);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching partido detalle:', error);
      // fallback: set the raw partido object
      setPartidoSeleccionado(partido);
    }
  };

  const handlePartidoChange = (e) => {
    setPartidoForm({ ...partidoForm, [e.target.name]: e.target.value });
  };

  const handleGolFormChange = (e) => {
    const { name, value } = e.target;
    setGolForm({ ...golForm, [name]: value });
  };

  const handleJugadoresSeleccionadosChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value);
    const jugadoresSeleccionadosData = selectedOptions.map(id => {
      const jugador = jugadores.find(j => String(j.id) === id);
      if (jugador) {
        return {
          id: jugador.id,
          nombre: jugador.nombre,
          posicion: jugador.posicion
        };
      }
      return null;
    }).filter(j => j !== null);
    setJugadoresSeleccionados(jugadoresSeleccionadosData);
  };


  const agregarEstadisticasJugador = () => {
    if (!golForm.jugadorId) return;

    const jugadorId = parseInt(golForm.jugadorId);
    const jugador = jugadoresSeleccionados.find(j => j.id === jugadorId);

    if (!jugador) return;

    // Verificar si ya tiene estadísticas
    const estadisticasExistentes = partidoForm.jugadoresParticipantes.find(j => j.jugadorId === jugadorId);

    if (estadisticasExistentes) {
      // Actualizar estadísticas existentes
      setPartidoForm({
        ...partidoForm,
        jugadoresParticipantes: partidoForm.jugadoresParticipantes.map(j =>
          j.jugadorId === jugadorId
            ? {
                ...j,
                minutosJugados: golForm.minutosJugados || j.minutosJugados,
                asistencias: golForm.asistencias || j.asistencias,
                tarjetasAmarillas: golForm.tarjetasAmarillas || j.tarjetasAmarillas,
                tarjetasRojas: golForm.tarjetasRojas || j.tarjetasRojas,
                tarjetasAzules: golForm.tarjetasAzules || j.tarjetasAzules
              }
            : j
        )
      });
    } else {
      // Agregar nuevas estadísticas
      setPartidoForm({
        ...partidoForm,
        jugadoresParticipantes: [
          ...partidoForm.jugadoresParticipantes,
          {
            jugadorId: jugadorId,
            minutosJugados: golForm.minutosJugados || 0,
            asistencias: golForm.asistencias || 0,
            tarjetasAmarillas: golForm.tarjetasAmarillas || 0,
            tarjetasRojas: golForm.tarjetasRojas || 0,
            tarjetasAzules: golForm.tarjetasAzules || 0
          }
        ]
      });
    }

    // Limpiar formulario
    setGolForm({
      jugadorId: '',
      minutosJugados: '',
      asistencias: '',
      tarjetasAmarillas: '',
      tarjetasRojas: '',
      tarjetasAzules: ''
    });
  };

  const agregarGolNuevoPartido = () => {
    if (!registrarGolForm.jugadorId) return;

    const jugadorId = parseInt(registrarGolForm.jugadorId, 10);
    if (!jugadoresSeleccionados.some(j => j.id === jugadorId)) return;

    setPartidoForm({
      ...partidoForm,
      goles: [
        ...partidoForm.goles,
        {
          jugadorId,
          tipoDeGol: registrarGolForm.tipoDeGol || 'NO_ESPECIFICADO',
          resultadoTrasGol: registrarGolForm.resultadoTrasGol,
          parte: registrarGolForm.parte
        }
      ]
    });

    const nextLocal = Math.min(parseInt(registrarGolForm.resultadoTrasGolLocal || 0, 10) + 1, 8);
    setRegistrarGolForm({
      ...registrarGolForm,
      tipoDeGol: 'NO_ESPECIFICADO',
      resultadoTrasGolLocal: String(nextLocal),
      resultadoTrasGol: `${nextLocal}-${registrarGolForm.resultadoTrasGolVisitante}`
    });
  };

  const eliminarGolNuevoPartido = (index) => {
    setPartidoForm({
      ...partidoForm,
      goles: partidoForm.goles.filter((_, i) => i !== index)
    });
  };

  const eliminarJugador = (index) => {
    const nuevosJugadores = partidoForm.jugadoresParticipantes.filter((_, i) => i !== index);
    setPartidoForm({ ...partidoForm, jugadoresParticipantes: nuevosJugadores });
  };

  const handleSubmitPartido = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const participantesMap = new Map();
      partidoForm.jugadoresParticipantes.forEach(j => participantesMap.set(j.jugadorId, j));
      jugadoresSeleccionados.forEach(j => {
        if (!participantesMap.has(j.id)) {
          participantesMap.set(j.id, {
            jugadorId: j.id,
            minutosJugados: 0,
            asistencias: 0,
            tarjetasAmarillas: 0,
            tarjetasRojas: 0,
            tarjetasAzules: 0
          });
        }
      });
      partidoForm.goles.forEach(g => {
        if (!participantesMap.has(g.jugadorId)) {
          participantesMap.set(g.jugadorId, {
            jugadorId: g.jugadorId,
            minutosJugados: 0,
            asistencias: 0,
            tarjetasAmarillas: 0,
            tarjetasRojas: 0,
            tarjetasAzules: 0
          });
        }
      });

      const payload = {
        ...partidoForm,
        temporadaId: parseInt(partidoForm.temporadaId, 10),
        noPresentadoRival: partidoForm.noPresentadoRival || false,
        noPresentadoNosotros: partidoForm.noPresentadoNosotros || false,
        golesPrimeraParte: partidoForm.golesPrimeraParte ? parseInt(partidoForm.golesPrimeraParte, 10) : null,
        golesSegundaParte: partidoForm.golesSegundaParte ? parseInt(partidoForm.golesSegundaParte, 10) : null,
        golesRivalPrimeraParte: partidoForm.golesRivalPrimeraParte ? parseInt(partidoForm.golesRivalPrimeraParte, 10) : null,
        golesRivalSegundaParte: partidoForm.golesRivalSegundaParte ? parseInt(partidoForm.golesRivalSegundaParte, 10) : null,
        numeroJornada: partidoForm.numeroJornada ? parseInt(partidoForm.numeroJornada, 10) : null,
        jugadoresParticipantes: Array.from(participantesMap.values()).map(j => ({
          jugadorId: j.jugadorId,
          minutosJugados: j.minutosJugados ? parseInt(j.minutosJugados, 10) : 0,
          asistencias: j.asistencias ? parseInt(j.asistencias, 10) : 0,
          tarjetasAmarillas: j.tarjetasAmarillas ? parseInt(j.tarjetasAmarillas, 10) : 0,
          tarjetasRojas: j.tarjetasRojas ? parseInt(j.tarjetasRojas, 10) : 0,
          tarjetasAzules: j.tarjetasAzules ? parseInt(j.tarjetasAzules, 10) : 0
        })),
        goles: partidoForm.goles.map(g => ({
          jugadorId: g.jugadorId,
          tipoDeGol: g.tipoDeGol,
          resultadoTrasGol: g.resultadoTrasGol,
          parte: g.parte
        }))
      };

      await axios.post(`${API_URL}/partidos/completo`, payload);
      await fetchRegistrarPartidos(payload.temporadaId);
      alert('Partido guardado exitosamente');
      setPartidoForm({
        temporadaId: '',
        fecha: '',
        lugar: '',
        rival: '',
        resultado: '',
        marcador: '',
        noPresentadoRival: false,
        noPresentadoNosotros: false,
        golesPrimeraParte: '',
        golesSegundaParte: '',
        golesRivalPrimeraParte: '',
        golesRivalSegundaParte: '',
        numeroJornada: '',
        idaVuelta: '',
        descripcion: '',
        jugadoresParticipantes: [],
        goles: []
      });
      setJugadoresSeleccionados([]);
      setGolForm({
        jugadorId: '',
        minutosJugados: '',
        tipoDeGol: '',
        resultadoTrasGol: '',
        parte: '',
        asistencias: '',
        tarjetasAmarillas: '',
        tarjetasRojas: '',
        tarjetasAzules: ''
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error guardando partido:', error);
      alert('Error al guardar el partido');
    } finally {
      setLoading(false);
    }
  };

  const renderBusquedaPartidosTab = () => {
    const rivales = [...new Set(
      partidosBusqueda
        .map(partido => partido.rival?.trim())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    const consulta = rivalBusqueda.trim().toLocaleLowerCase('es');
    const rivalExacto = rivales.find(rival => rival.toLocaleLowerCase('es') === consulta);
    const partidosFiltrados = consulta
      ? partidosBusqueda
        .filter(partido => rivalExacto
          ? partido.rival?.toLocaleLowerCase('es') === consulta
          : partido.rival?.toLocaleLowerCase('es').includes(consulta))
        .sort((a, b) => {
          const temporadaOrden = String(b.temporadaAño).localeCompare(String(a.temporadaAño), 'es');
          return temporadaOrden || String(b.fecha || '').localeCompare(String(a.fecha || ''));
        })
      : [];

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Search className="h-7 w-7 text-blue-600" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Búsqueda de partidos</h2>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <label htmlFor="rival-busqueda" className="block text-sm font-medium text-gray-700 mb-2">
            Buscar rival
          </label>
          <input
            id="rival-busqueda"
            type="search"
            list="rivales-disponibles"
            value={rivalBusqueda}
            onChange={e => setRivalBusqueda(e.target.value)}
            placeholder="Escribe o selecciona un equipo..."
            className="w-full md:max-w-xl border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <datalist id="rivales-disponibles">
            {rivales.map(rival => <option key={rival} value={rival} />)}
          </datalist>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          {loadingBusquedaPartidos ? (
            <div className="p-6 text-center text-gray-500">Cargando partidos...</div>
          ) : errorBusquedaPartidos ? (
            <div className="p-6 text-center text-red-600">{errorBusquedaPartidos}</div>
          ) : !consulta ? (
            <div className="p-6 text-center text-gray-500">Escribe o selecciona un rival para ver los partidos.</div>
          ) : partidosFiltrados.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No se encontraron partidos contra «{rivalBusqueda}».</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Temporada</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Rival</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Resultado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Marcador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Detalle</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {partidosFiltrados.map(partido => (
                  <tr key={partido.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{partido.temporadaAño}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{partido.fecha || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{partido.rival}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{partido.resultado || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{partido.marcador || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/historico/${partido.temporadaId}/${partido.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Ver partido →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderHistoricoTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex items-center space-x-3">
          <History className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600 shrink-0" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Histórico</h2>
        </div>
      </div>

      {!temporadaSeleccionada ? (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          {temporadas.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No hay temporadas cargadas.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Año</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Puesto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Puntos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">PJ</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">PG</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">PE</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">PP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">GF</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">GC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">+/-</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">GFPP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">GCPP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Sin Marcar</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Sin Encajar</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Min Goleados</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Max Goleador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Pichichi</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Asistente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Amonestado</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {temporadas.map((temporada, index) => {
                  const gf = temporada.goles_a_favor || 0;
                  const gc = temporada.goles_en_contra || 0;
                  const pj = temporada.partidos_jugados || 0;
                  const diferencia = gf - gc;
                  const gfpp = pj > 0 ? (gf / pj).toFixed(2) : '-';
                  const gcpp = pj > 0 ? (gc / pj).toFixed(2) : '-';

                  return (
                    <tr
                      key={temporada.id}
                      className={`hover:bg-blue-50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      onClick={() => handleTemporadaClick(temporada)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{temporada.año}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 relative">
                        {temporada.puesto_final ? (
                          <>
                            {temporada.puesto_final === 1 && <img src="/medalla-oro-16.png" alt="Oro" className="absolute left-1 top-1/2 -translate-y-1/2 h-4 w-4" />}
                            {temporada.puesto_final === 2 && <img src="/medalla-plata-16.png" alt="Plata" className="absolute left-1 top-1/2 -translate-y-1/2 h-4 w-4" />}
                            {temporada.puesto_final === 3 && <img src="/medalla-bronce-16.png" alt="Bronce" className="absolute left-1 top-1/2 -translate-y-1/2 h-4 w-4" />}
                            <span className="pl-5">{temporada.puesto_final}º</span>
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.puntos || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.partidos_jugados || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.partidos_ganados || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.partidos_empatados || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.partidos_perdidos || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.goles_a_favor || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.goles_en_contra || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{diferencia}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gfpp}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gcpp}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.partidos_sin_marcar || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.partidos_sin_encajar || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.min_goleados || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.max_goleador || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.pichichi?.nombre || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.asistente?.nombre || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{temporada.amonestado?.nombre || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (process.env.NODE_ENV !== 'production') console.log('Click en Volver al histórico');
                window.location.href = '/historico';
              }}
              className="text-blue-600 hover:text-blue-800 flex items-center cursor-pointer shrink-0"
              style={{ cursor: 'pointer' }}
            >
              ← Volver al histórico
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
              Partidos de la temporada {temporadaSeleccionada.año}
            </h3>
          </div>

          {!partidoSeleccionado ? (
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              {partidosTemporada.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No hay partidos en esta temporada.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Rival</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Resultado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Marcador</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Lugar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Jornada</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Ida/Vuelta</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {partidosTemporada.map((partido, index) => {
                      const resultadoColor = getResultadoColor(partido.resultado);
                      return (
                        <tr
                          key={partido.id}
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                          onClick={() => handlePartidoClick(partido)}
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{partido.fecha}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{partido.rival}</td>
                          <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${resultadoColor}`}>{partido.resultado}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{partido.marcador || '-'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{partido.lugar}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{partido.numeroJornada || '-'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{partido.idaVuelta || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (process.env.NODE_ENV !== 'production') console.log('Click en Volver a partidos');
                    window.location.href = `/historico/${temporadaSeleccionada.id}`;
                  }}
                  className="text-blue-600 hover:text-blue-800 flex items-center cursor-pointer shrink-0"
                  style={{ cursor: 'pointer' }}
                >
                  ← Volver a partidos
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  Detalle del partido vs {partidoSeleccionado.rival}
                </h3>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Fecha</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.fecha}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Resultado</label>
                    <p className="text-sm text-gray-900 font-medium">{partidoSeleccionado.resultado}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Lugar</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.lugar}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Jornada</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.numeroJornada || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Goles 1ª Parte</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.golesPrimeraParte ?? 0}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Goles 2ª Parte</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.golesSegundaParte ?? 0}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Goles Rival 1ª Parte</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.golesRivalPrimeraParte ?? 0}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Goles Rival 2ª Parte</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.golesRivalSegundaParte ?? 0}</p>
                  </div>
                </div>

                {partidoSeleccionado.descripcion && (
                  <div className="mb-6">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Descripción</label>
                    <p className="text-sm text-gray-900">{partidoSeleccionado.descripcion}</p>
                  </div>
                )}

                <h4 className="text-lg font-semibold text-gray-800 mb-4">Jugadores y resumen</h4>
                {partidoSeleccionado.participacionesJugadores && partidoSeleccionado.participacionesJugadores.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Jugador</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Stats</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {partidoSeleccionado.participacionesJugadores.map((jp, idx) => {
                        // Get resultadoTrasGol for this player's goals
                        const playerGoals = (partidoSeleccionado.goles || []).filter(g => g.jugadorId === jp.id);
                        const resultadosTrasGol = playerGoals.map(g => g.resultadoTrasGol).filter(r => r);
                        
                        return (
                          <tr key={idx}>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {jp.jugador?.nombre || '-'}
                              {resultadosTrasGol.length > 0 && (
                                <span className="ml-2 text-xs text-blue-600">
                                  ({resultadosTrasGol.join(', ')})
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {/* Goals: one ball per goal (limit repeats to 10, otherwise show count) */}
                              {jp.golesTotales > 0 ? (
                                jp.golesTotales <= 10 ? ' ' + '⚽'.repeat(jp.golesTotales) : ` ⚽ x${jp.golesTotales}`
                              ) : null}
                              {/* Assists: footprints icon per assist */}
                              {jp.asistencias > 0 ? (
                                <span className="inline-flex items-center">
                                  {jp.asistencias <= 10 ? (
                                    Array.from({ length: jp.asistencias }).map((_, i) => (
                                      <Footprints key={i} className="h-4 w-4 inline ml-1 text-green-600" />
                                    ))
                                  ) : (
                                    <span className="ml-1 text-green-600">
                                      <Footprints className="h-4 w-4 inline" /> x{jp.asistencias}
                                    </span>
                                  )}
                                </span>
                              ) : null}
                              {/* Cards */}
                              {jp.tarjetasAmarillas > 0 ? ' ' + '🟨' : null}
                              {jp.tarjetasRojas > 0 ? ' ' + '🟥' : null}
                              {/* Minutes */}
                              {' '}
                              <span className="ml-2 text-xs text-gray-400">{jp.minutosJugados ? `${jp.minutosJugados} min` : ''}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-500">No hay jugadores registrados en este partido.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderResumenTemporada = (temporada) => {
    const historicalSeasons = [
      '2007-2008', '2008-2009', '2009-2010', '2010-2011', '2011-2012',
      '2012-2013', '2013-2014', '2014-2015', '2015-2016', '2016-2017',
      '2017-2018', '2018-2019', '2019-2020', '2020-2021', '2021-2022',
      '2022-2023', '2023-2024', '2024-2025', '2025-2026'
    ];
    const useCalculado = !historicalSeasons.includes(temporada.año);
    
    const gf = useCalculado && resumenCalculado ? (resumenCalculado.goles_a_favor || 0) : (temporada.goles_a_favor || 0);
    const gc = useCalculado && resumenCalculado ? (resumenCalculado.goles_en_contra || 0) : (temporada.goles_en_contra || 0);
    const pj = useCalculado && resumenCalculado ? (resumenCalculado.partidos_jugados || 0) : (temporada.partidos_jugados || 0);
    const pg = useCalculado && resumenCalculado ? (resumenCalculado.partidos_ganados || 0) : (temporada.partidos_ganados || 0);
    const pe = useCalculado && resumenCalculado ? (resumenCalculado.partidos_empatados || 0) : (temporada.partidos_empatados || 0);
    const pp = useCalculado && resumenCalculado ? (resumenCalculado.partidos_perdidos || 0) : (temporada.partidos_perdidos || 0);
    const sinMarcar = useCalculado && resumenCalculado ? (resumenCalculado.partidos_sin_marcar || 0) : (temporada.partidos_sin_marcar || 0);
    const sinEncajar = useCalculado && resumenCalculado ? (resumenCalculado.partidos_sin_encajar || 0) : (temporada.partidos_sin_encajar || 0);
    
    const diferencia = gf != null && gc != null ? gf - gc : '-';
    const gfpp = pj > 0 && gf != null ? (gf / pj).toFixed(2) : '-';
    const gcpp = pj > 0 && gc != null ? (gc / pj).toFixed(2) : '-';

    const campos = [
      { label: 'Puesto', value: mostrarValor(temporada.puesto_final) },
      { label: 'Puntos', value: mostrarValor(temporada.puntos) },
      { label: 'PJ', value: mostrarValor(pj) },
      { label: 'PG', value: mostrarValor(pg) },
      { label: 'PE', value: mostrarValor(pe) },
      { label: 'PP', value: mostrarValor(pp) },
      { label: 'GF', value: mostrarValor(gf) },
      { label: 'GC', value: mostrarValor(gc) },
      { label: '+/-', value: diferencia },
      { label: 'GF/PJ', value: gfpp },
      { label: 'GC/PJ', value: gcpp },
      { label: 'Sin Marcar', value: mostrarValor(sinMarcar) },
      { label: 'Sin Encajar', value: mostrarValor(sinEncajar) },
      { label: 'Min Goleados', value: mostrarValor(temporada.min_goleados) },
      { label: 'Max Goleador', value: mostrarValor(temporada.max_goleador) },
      { label: 'Pichichi', value: temporada.pichichi?.nombre || '-' },
      { label: 'Asistente', value: temporada.asistente?.nombre || '-' },
      { label: 'Amonestado', value: temporada.amonestado?.nombre || '-' },
    ];

    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {campos.map(campo => (
            <div key={campo.label}>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{campo.label}</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">{campo.value}</p>
            </div>
          ))}
        </div>
        {temporada.descripcion && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</p>
            <p className="text-sm text-gray-700 mt-1">{temporada.descripcion}</p>
          </div>
        )}
      </div>
    );
  };

  const renderTemporadaVistaTab = () => {
    const historicalSeasons = [
      '2007-2008', '2008-2009', '2009-2010', '2010-2011', '2011-2012',
      '2012-2013', '2013-2014', '2014-2015', '2015-2016', '2016-2017',
      '2017-2018', '2018-2019', '2019-2020', '2020-2021', '2021-2022',
      '2022-2023', '2023-2024', '2024-2025', '2025-2026'
    ];
    const useCalculado = temporadaVista && !historicalSeasons.includes(temporadaVista.año);
    const estadisticasOrdenadas = ordenarEstadisticasTemporada(useCalculado ? estadisticasCalculadas : estadisticasTemporadaVista);
    const loading = useCalculado ? loadingEstadisticasCalculadas : loadingEstadisticasTemporada;

    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Temporada</h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            <label htmlFor="temporada-select" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Temporada:
            </label>
            <select
              id="temporada-select"
              value={temporadaVistaId}
              onChange={(e) => handleTemporadaVistaChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:min-w-[160px] sm:w-auto"
            >
              {temporadas.length === 0 ? (
                <option value="">Sin temporadas</option>
              ) : (
                temporadas.map(t => (
                  <option key={t.id} value={t.id}>{t.año}</option>
                ))
              )}
            </select>
          </div>
        </div>

        {!temporadaVista ? (
          <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
            No hay temporadas cargadas.
          </div>
        ) : (
          <>
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  Resumen {temporadaVista.año}
                </h3>
                <Link
                  to={`/historico/${temporadaVista.id}`}
                  onClick={() => handleTemporadaClick(temporadaVista)}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Ver partidos de la temporada →
                </Link>
              </div>
              {renderResumenTemporada(temporadaVista)}
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Jugadores</h3>
              <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                {loading ? (
                  <div className="p-6 text-center text-gray-500">Cargando estadísticas...</div>
                ) : estadisticasOrdenadas.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No hay jugadores registrados en esta temporada.
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Nombre jugador</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">PJ</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Goles</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Pie derecho</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Pie izquierdo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Cabeza</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Balón Parado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Goles/PJ</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Asistencias</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Amarillas</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Expulsiones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {estadisticasOrdenadas.map((est) => (
                        <tr key={est.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {est.jugador?.nombre || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.partidosJugados || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{est.golesTotales || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesPieDerecho || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesPieIzquierdo || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesCabeza || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesBalonParado || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {calcularGolesPorPartido(est.golesTotales || 0, est.partidosJugados || 0)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.asistencias || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.tarjetasAmarillas || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{calcularExpulsiones(est)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderAllMnlsTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex items-center space-x-3">
          <Trophy className="h-7 w-7 sm:h-8 sm:w-8 text-yellow-500 shrink-0" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">All MNLS</h2>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        {loadingEstadisticasAgregadas ? (
          <div className="p-6 text-center text-gray-500">Cargando estadísticas...</div>
        ) : estadisticasAgregadas.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No hay estadísticas agregadas disponibles.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-blue-600 to-blue-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Nombre jugador</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">PJ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Goles</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Pie derecho</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Pie izquierdo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Cabeza</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Balón Parado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Goles/PJ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Asistencias</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Amarillas</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">Expulsiones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {estadisticasAgregadas.map((est, index) => (
                <tr
                  key={est.jugadorId}
                  className={`hover:bg-blue-50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  onClick={() => irADetalleJugador(est.jugadorId)}
                >
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-700 hover:text-blue-900 flex items-center">
                    <Shirt className="h-4 w-4 mr-2 text-gray-400" />
                    {est.nombre}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.partidosJugados || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">{est.golesTotales || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesPieDerecho || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesPieIzquierdo || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesCabeza || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.golesBalonParado || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {calcularGolesPorPartido(est.golesTotales || 0, est.partidosJugados || 0)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.asistencias || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.tarjetasAmarillas || 0}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{est.expulsiones || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderDetalleJugadorTab = () => {
    const jugadorSeleccionado = jugadores.find(j => j.id === parseInt(jugadorEstadisticasId));
    const totales = calcularTotalesEstadisticas(estadisticasJugador);

    return (
      <div className="space-y-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Detalle del Jugador</h2>

        <div className="bg-white rounded-lg shadow-md p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar jugador</label>
          <select
            value={jugadorEstadisticasId}
            onChange={handleJugadorEstadisticasChange}
            className="w-full md:w-96 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Seleccionar jugador</option>
            {jugadores.map(j => (
              <option key={j.id} value={j.id}>{j.nombre}{j.posicion ? ` (${j.posicion})` : ''}</option>
            ))}
          </select>
        </div>

        {!jugadorEstadisticasId ? (
          <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
            Selecciona un jugador para ver sus estadísticas por temporada.
          </div>
        ) : loadingEstadisticas ? (
          <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
            Cargando estadísticas...
          </div>
        ) : (
          <>
            {recordsJugador && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Records Personales</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Ligas Ganadas */}
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-yellow-800">Ligas Ganadas</span>
                      <Trophy className="h-5 w-5 text-yellow-600" />
                    </div>
                    <p className="text-2xl font-bold text-yellow-900">{recordsJugador.ligasGanadas || 0}</p>
                  </div>

                  {/* Pichichis */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-800">Pichichis</span>
                      <Award className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-900">{recordsJugador.pichichis?.length || 0}</p>
                    {recordsJugador.pichichis && recordsJugador.pichichis.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {recordsJugador.pichichis.map((pichichi, idx) => (
                          <div key={idx} className="flex items-center text-xs text-green-700">
                            <Footprints className="h-3 w-3 mr-1" />
                            <span>{pichichi.año}: {pichichi.goles} goles</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Máximo Asistente */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">Máx. Asistente</span>
                      <Award className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{recordsJugador.maximosAsistente?.length || 0}</p>
                    {recordsJugador.maximosAsistente && recordsJugador.maximosAsistente.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {recordsJugador.maximosAsistente.map((asistente, idx) => (
                          <div key={idx} className="flex items-center text-xs text-blue-700">
                            <Shirt className="h-3 w-3 mr-1" />
                            <span>{asistente.año}: {asistente.asistencias} asistencias</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Máximo Amonestado */}
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-red-800">Máx. Amonestado</span>
                      <Award className="h-5 w-5 text-red-600" />
                    </div>
                    <p className="text-2xl font-bold text-red-900">{recordsJugador.maximosAmonestado?.length || 0}</p>
                    {recordsJugador.maximosAmonestado && recordsJugador.maximosAmonestado.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {recordsJugador.maximosAmonestado.map((amonestado, idx) => (
                          <div key={idx} className="flex items-center text-xs text-red-700">
                            <Shield className="h-3 w-3 mr-1" />
                            <span>{amonestado.año}: {amonestado.tarjetas} tarjetas</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {jugadorSeleccionado?.nombre || 'Jugador'}
                </h3>
                {jugadorSeleccionado?.posicion && (
                  <p className="text-sm text-gray-500 mt-1">{jugadorSeleccionado.posicion}</p>
                )}
              </div>

            {estadisticasJugador.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No hay estadísticas registradas para este jugador.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Temporada</th>
                    {CAMPOS_ESTADISTICAS.map(campo => (
                      <th key={campo.key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {campo.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {estadisticasJugador.map((est) => (
                    <tr key={est.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {est.temporada?.año || '-'}
                      </td>
                      {CAMPOS_ESTADISTICAS.map(campo => (
                        <td key={campo.key} className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {est[campo.key] || 0}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">TOTAL</td>
                    {CAMPOS_ESTADISTICAS.map(campo => (
                      <td key={campo.key} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {totales[campo.key]}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          </>
        )}
      </div>
    );
  };

  const renderRegistrarPartidoTab = () => {
    if (registrarNuevoPartido) return renderNuevoPartidoForm();

    return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Registrar Participación</h2>
        <button
          type="button"
          onClick={iniciarNuevoPartido}
          className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-semibold"
        >
          Nuevo partido
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {errorMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temporada</label>
            <select
              value={registrarTemporadaId}
              onChange={(e) => handleRegistrarTemporadaChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Seleccionar temporada</option>
              {temporadas.map(t => (
                <option key={t.id} value={t.id}>{t.año}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Partido</label>
            <select
              value={registrarPartidoId}
              onChange={(e) => handleRegistrarPartidoChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={!registrarTemporadaId}
            >
              <option value="">Seleccionar partido</option>
              {registrarPartidos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.fecha} — vs {p.rival} ({p.resultado || 'Sin resultado'})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!registrarPartidoId ? (
        <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
          Selecciona una temporada y un partido para registrar jugadores y goleadores.
        </div>
      ) : (
        <form onSubmit={handleSubmitParticipacion} className="space-y-6">
          {registrarPartidoDetalle && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">
                {registrarPartidoDetalle.fecha} — vs {registrarPartidoDetalle.rival}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Resultado</p>
                  <p className="text-sm font-semibold text-gray-900">{registrarPartidoDetalle.resultado || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Marcador</p>
                  <p className="text-sm text-gray-900">{registrarPartidoDetalle.marcador || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Lugar</p>
                  <p className="text-sm text-gray-900">{registrarPartidoDetalle.lugar || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Jornada</p>
                  <p className="text-sm text-gray-900">{registrarPartidoDetalle.numeroJornada || '-'}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Jugadores que participaron</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar jugadores (múltiple)</label>
              <select
                multiple
                size={8}
                value={registrarJugadoresSeleccionados.map(j => j.id)}
                onChange={handleRegistrarJugadoresSeleccionadosChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {jugadores.map(j => (
                  <option key={j.id} value={j.id}>{j.nombre} ({j.posicion || 'Sin posición'})</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Mantén presionada Ctrl/Cmd para seleccionar múltiples jugadores</p>
            </div>

            {registrarJugadoresSeleccionados.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {registrarJugadoresSeleccionados.map(j => (
                  <div key={j.id} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    {j.nombre} ({j.posicion || '-'})
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Estadísticas por jugador</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Jugador</label>
                <div className="space-y-2">
                  {registrarJugadoresSeleccionados.map(j => (
                    <label key={j.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                       name="jugadorIdEstadisticas"
                       value={String(j.id)}
                       checked={registrarGolForm.jugadorId === String(j.id)}
                       onChange={(e) => {
                         if (e.target.checked) {
                           setRegistrarGolForm({ ...registrarGolForm, jugadorId: String(j.id) });
                         }
                       }}
                       className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                     />
                     <span className="text-sm text-gray-700">{j.nombre}</span>
                   </label>
                 ))}
               </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minutos jugados</label>
                <input
                  type="number"
                  name="minutosJugados"
                  value={registrarGolForm.minutosJugados}
                  onChange={handleRegistrarGolFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asistencias</label>
                <input
                  type="number"
                  name="asistencias"
                  value={registrarGolForm.asistencias}
                  onChange={handleRegistrarGolFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarjetas amarillas</label>
                <input
                  type="number"
                  name="tarjetasAmarillas"
                  value={registrarGolForm.tarjetasAmarillas}
                  onChange={handleRegistrarGolFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarjetas rojas</label>
                <input
                  type="number"
                  name="tarjetasRojas"
                  value={registrarGolForm.tarjetasRojas}
                  onChange={handleRegistrarGolFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarjetas azules</label>
                <input
                  type="number"
                  name="tarjetasAzules"
                  value={registrarGolForm.tarjetasAzules}
                  onChange={handleRegistrarGolFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={agregarEstadisticasRegistrar}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Agregar estadísticas
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Goleadores</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Jugador</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {registrarJugadoresSeleccionados.map(j => (
                  <label key={j.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                     name="jugadorIdGoleadores"
                     value={String(j.id)}
                      checked={registrarGolForm.jugadorId === String(j.id)}
                     onChange={(e) => {
                       if (e.target.checked) {
                         setRegistrarGolForm({ ...registrarGolForm, jugadorId: String(j.id) });
                       }
                     }}
                     className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                   />
                   <span className="text-sm text-gray-700">{j.nombre}</span>
                 </label>
               ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de gol</label>
                <select
                  name="tipoDeGol"
                  value={registrarGolForm.tipoDeGol}
                  onChange={handleRegistrarGolFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seleccionar</option>
                  <option value="PIE_DERECHO">Pie derecho</option>
                  <option value="PIE_IZQUIERDO">Pie izquierdo</option>
                  <option value="CABEZA">Cabeza</option>
                  <option value="BALON_PARADO">Balón parado</option>
                  <option value="NO_ESPECIFICADO">No especificado</option>
                  <option value="PROPIA_META">Propia meta</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Parte</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="parte"
                      value=""
                      checked={registrarGolForm.parte === ''}
                      onChange={handleRegistrarGolFormChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">No especificado</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="parte"
                      value="1"
                      checked={registrarGolForm.parte === '1'}
                      onChange={handleRegistrarGolFormChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">1ª parte</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="parte"
                      value="2"
                      checked={registrarGolForm.parte === '2'}
                      onChange={handleRegistrarGolFormChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">2ª parte</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">Resultado tras gol</label>
              <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-blue-700 mb-2">👥 Nuestro equipo</p>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <label key={`local-${num}`} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="resultadoTrasGolLocal"
                            value={String(num)}
                            checked={registrarGolForm.resultadoTrasGolLocal === String(num)}
                            onChange={handleRegistrarGolFormChange}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 w-5 text-center font-semibold">{num}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-red-700 mb-2">🎯 Equipo rival</p>
                    <div className="flex flex-wrap gap-2">
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <label key={`visitante-${num}`} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="resultadoTrasGolVisitante"
                            value={String(num)}
                            checked={registrarGolForm.resultadoTrasGolVisitante === String(num)}
                            onChange={handleRegistrarGolFormChange}
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                          />
                          <span className="text-sm text-gray-700 w-5 text-center font-semibold">{num}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    name="resultadoTrasGol"
                    placeholder="Ej: 5-2, 10-0 (para resultados personalizados)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={registrarGolForm.resultadoTrasGol}
                    onChange={handleRegistrarGolFormChange}
                  />
                </div>
            </div>
            <button
              type="button"
              onClick={agregarGolRegistrar}
              className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
            >
              Agregar gol
            </button>

            {registrarForm.goles.length > 0 && (
              <div className="mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jugador</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resultado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parte</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {registrarForm.goles.map((gol, index) => {
                      const jugador = registrarJugadoresSeleccionados.find(j => j.id === gol.jugadorId)
                        || jugadores.find(j => j.id === gol.jugadorId);
                      return (
                        <tr key={index}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{jugador?.nombre || 'Desconocido'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gol.tipoDeGol}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gol.resultadoTrasGol || '-'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gol.parte || '-'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            <button
                              type="button"
                              onClick={() => eliminarGolRegistrar(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {registrarForm.jugadoresParticipantes.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Estadísticas registradas</h3>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Jugador</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Minutos</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Asistencias</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Amarillas</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Rojas</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Azules</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {registrarForm.jugadoresParticipantes.map((jp, index) => {
                    const jugador = registrarJugadoresSeleccionados.find(j => j.id === jp.jugadorId)
                      || jugadores.find(j => j.id === jp.jugadorId);
                    return (
                      <tr key={index}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{jugador?.nombre || 'Desconocido'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.minutosJugados ?? '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.asistencias ?? '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.tarjetasAmarillas ?? '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.tarjetasRojas ?? '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.tarjetasAzules ?? '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            type="button"
                            onClick={() => eliminarJugadorRegistrar(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <button
            type="submit"
            disabled={loadingRegistrar}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-semibold"
          >
            {loadingRegistrar ? 'Guardando...' : 'Guardar participación'}
          </button>
        </form>
      )}
    </div>
    );
  };

  const renderRecordsTab = () => (
    <div className="space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Records MNLS</h2>
      
      {/* Records del Equipo */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center gap-2">
          <Trophy className="h-6 w-6 text-blue-600" />
          Records del Equipo
        </h3>
        
        {loadingRecordsEquipo ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Cargando records del equipo...</p>
          </div>
        ) : !recordsEquipo ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">No hay datos de records del equipo disponibles.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Resumen General */}
            <div>
              <h4 className="text-md font-semibold mb-3 text-gray-600">Resumen General</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{recordsEquipo.partidosGanados || 0}</p>
                  <p className="text-sm text-gray-600">Partidos Ganados</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{recordsEquipo.partidosPerdidos || 0}</p>
                  <p className="text-sm text-gray-600">Partidos Perdidos</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-600">{recordsEquipo.partidosEmpatados || 0}</p>
                  <p className="text-sm text-gray-600">Partidos Empatados</p>
                </div>
              </div>
            </div>

            {/* Records de Partidos */}
            <div>
              <h4 className="text-md font-semibold mb-3 text-gray-600">Records de Partidos</h4>
              <div className="space-y-4">
                {recordsEquipo.victoriaMasGoles && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Victoria con más goles a favor</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.victoriaMasGoles.año} - vs {recordsEquipo.victoriaMasGoles.rival}
                    </p>
                    <p className="text-sm text-gray-600">Resultado: {recordsEquipo.victoriaMasGoles.resultado} ({recordsEquipo.victoriaMasGoles.marcador})</p>
                    <Link
                      to={`/historico/${recordsEquipo.victoriaMasGoles.temporadaId}/${recordsEquipo.victoriaMasGoles.partidoId}`}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Ver detalle del partido
                    </Link>
                  </div>
                )}
                {recordsEquipo.derrotaMasGoles && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Derrota con más goles en contra</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.derrotaMasGoles.año} - vs {recordsEquipo.derrotaMasGoles.rival}
                    </p>
                    <p className="text-sm text-gray-600">Resultado: {recordsEquipo.derrotaMasGoles.resultado} ({recordsEquipo.derrotaMasGoles.marcador})</p>
                    <Link
                      to={`/historico/${recordsEquipo.derrotaMasGoles.temporadaId}/${recordsEquipo.derrotaMasGoles.partidoId}`}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Ver detalle del partido
                    </Link>
                  </div>
                )}
                {recordsEquipo.partidoMasGoles && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Partido con más goles</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.partidoMasGoles.año} - vs {recordsEquipo.partidoMasGoles.rival}
                    </p>
                    <p className="text-sm text-gray-600">Resultado: {recordsEquipo.partidoMasGoles.resultado} ({recordsEquipo.partidoMasGoles.marcador})</p>
                    <Link
                      to={`/historico/${recordsEquipo.partidoMasGoles.temporadaId}/${recordsEquipo.partidoMasGoles.partidoId}`}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Ver detalle del partido
                    </Link>
                  </div>
                )}
                {recordsEquipo.resultadoMasRepetido && (
                  <div className="pb-4">
                    <p className="font-medium text-gray-800">Resultado más repetido</p>
                    <p className="text-sm text-gray-600">
                      {recordsEquipo.resultadoMasRepetido.resultado} - {recordsEquipo.resultadoMasRepetido.cantidad} veces
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Rachas */}
            <div>
              <h4 className="text-md font-semibold mb-3 text-gray-600">Rachas</h4>
              <div className="space-y-4">
                {recordsEquipo.mayorRachaGanados && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Mayor racha de partidos ganados</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.mayorRachaGanados.año} - {recordsEquipo.mayorRachaGanados.longitud} partidos
                    </p>
                    {recordsEquipo.mayorRachaGanados.partidos && recordsEquipo.mayorRachaGanados.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos de la racha:</p>
                        <div className="space-y-1">
                          {recordsEquipo.mayorRachaGanados.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>vs {partido.rival}</span>
                              <span className="text-green-600 font-medium">({partido.resultado})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {recordsEquipo.mayorRachaPerdidos && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Mayor racha de partidos perdidos</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.mayorRachaPerdidos.año} - {recordsEquipo.mayorRachaPerdidos.longitud} partidos
                    </p>
                    {recordsEquipo.mayorRachaPerdidos.partidos && recordsEquipo.mayorRachaPerdidos.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos de la racha:</p>
                        <div className="space-y-1">
                          {recordsEquipo.mayorRachaPerdidos.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>vs {partido.rival}</span>
                              <span className="text-red-600 font-medium">({partido.resultado})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {recordsEquipo.mayorRachaMarcando && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Mayor racha marcando goles</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.mayorRachaMarcando.año} - {recordsEquipo.mayorRachaMarcando.longitud} partidos
                    </p>
                    {recordsEquipo.mayorRachaMarcando.partidos && recordsEquipo.mayorRachaMarcando.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos de la racha:</p>
                        <div className="space-y-1">
                          {recordsEquipo.mayorRachaMarcando.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>vs {partido.rival}</span>
                              <span className="text-green-600 font-medium">({partido.resultado})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {recordsEquipo.mayorRachaSinMarcar && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Mayor racha sin marcar</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.mayorRachaSinMarcar.año} - {recordsEquipo.mayorRachaSinMarcar.longitud} partidos
                    </p>
                    {recordsEquipo.mayorRachaSinMarcar.partidos && recordsEquipo.mayorRachaSinMarcar.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos de la racha:</p>
                        <div className="space-y-1">
                          {recordsEquipo.mayorRachaSinMarcar.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>vs {partido.rival}</span>
                              <span className="text-red-600 font-medium">({partido.resultado})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {recordsEquipo.mayorRachaSinEncajar && (
                  <div className="pb-4">
                    <p className="font-medium text-gray-800">Mayor racha sin encajar gol</p>
                    <p className="text-sm text-gray-600">
                      Temporada {recordsEquipo.mayorRachaSinEncajar.año} - {recordsEquipo.mayorRachaSinEncajar.longitud} partidos
                    </p>
                    {recordsEquipo.mayorRachaSinEncajar.partidos && recordsEquipo.mayorRachaSinEncajar.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos de la racha:</p>
                        <div className="space-y-1">
                          {recordsEquipo.mayorRachaSinEncajar.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>vs {partido.rival}</span>
                              <span className="text-green-600 font-medium">({partido.resultado})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-md font-semibold mb-3 text-gray-600">Records de Rivales</h4>
              <div className="space-y-4">
                {recordsEquipo.rivalMasEnfrentamientos && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Rival más enfrentamientos</p>
                    <p className="text-sm text-gray-600">
                      {recordsEquipo.rivalMasEnfrentamientos.rival} - {recordsEquipo.rivalMasEnfrentamientos.cantidad} partidos
                    </p>
                    {recordsEquipo.rivalMasEnfrentamientos.partidos && recordsEquipo.rivalMasEnfrentamientos.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos:</p>
                        <div className="space-y-1">
                          {recordsEquipo.rivalMasEnfrentamientos.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>Temporada {partido.año}</span>
                              <span className="text-gray-500">vs {partido.rival}</span>
                              <span className="text-green-600 font-medium">({partido.marcador || '-'})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {recordsEquipo.rivalMasVictorias && (
                  <div className="border-b border-gray-200 pb-4">
                    <p className="font-medium text-gray-800">Rival más victorias</p>
                    <p className="text-sm text-gray-600">
                      {recordsEquipo.rivalMasVictorias.rival} - {recordsEquipo.rivalMasVictorias.cantidad} victorias
                    </p>
                    {recordsEquipo.rivalMasVictorias.partidos && recordsEquipo.rivalMasVictorias.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos:</p>
                        <div className="space-y-1">
                          {recordsEquipo.rivalMasVictorias.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>Temporada {partido.año}</span>
                              <span className="text-gray-500">vs {partido.rival}</span>
                              <span className="text-green-600 font-medium">({partido.marcador || '-'})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {recordsEquipo.rivalMasDerrotas && (
                  <div className="pb-4">
                    <p className="font-medium text-gray-800">Rival más derrotas</p>
                    <p className="text-sm text-gray-600">
                      {recordsEquipo.rivalMasDerrotas.rival} - {recordsEquipo.rivalMasDerrotas.cantidad} derrotas
                    </p>
                    {recordsEquipo.rivalMasDerrotas.partidos && recordsEquipo.rivalMasDerrotas.partidos.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Partidos:</p>
                        <div className="space-y-1">
                          {recordsEquipo.rivalMasDerrotas.partidos.map((partido, idx) => (
                            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                              <span>Temporada {partido.año}</span>
                              <span className="text-gray-500">vs {partido.rival}</span>
                              <span className="text-red-600 font-medium">({partido.marcador || '-'})</span>
                              <Link
                                to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Ver detalle
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Records Individuales */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center gap-2">
          <Award className="h-6 w-6 text-blue-600" />
          Records Individuales
        </h3>
        
        {loadingRecordsIndividuales ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Cargando records individuales...</p>
          </div>
        ) : !recordsIndividuales ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">No hay datos de records individuales disponibles.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Más premios Pichichi */}
            {recordsIndividuales.masPremiosPichichi && recordsIndividuales.masPremiosPichichi.length > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">Más premios Pichichi</span>
                  <Award className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.masPremiosPichichi.map((r, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold text-yellow-900">{r.nombre}</p>
                      <p className="text-sm text-yellow-800">{r.cantidad} veces</p>
                      <p className="text-xs text-yellow-700 mt-1">Años: {r.años?.join(', ') || '-'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Más premios Asistente */}
            {recordsIndividuales.masPremiosAsistente && recordsIndividuales.masPremiosAsistente.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">Más premios Asistente</span>
                  <Award className="h-5 w-5 text-blue-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.masPremiosAsistente.map((r, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold text-blue-900">{r.nombre}</p>
                      <p className="text-sm text-blue-800">{r.cantidad} veces</p>
                      <p className="text-xs text-blue-700 mt-1">Años: {r.años?.join(', ') || '-'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Más premios Amonestado */}
            {recordsIndividuales.masPremiosAmonestado && recordsIndividuales.masPremiosAmonestado.length > 0 && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-red-800">Más premios Amonestado</span>
                  <Award className="h-5 w-5 text-red-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.masPremiosAmonestado.map((r, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold text-red-900">{r.nombre}</p>
                      <p className="text-sm text-red-800">{r.cantidad} veces</p>
                      <p className="text-xs text-red-700 mt-1">Años: {r.años?.join(', ') || '-'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Más Hat-tricks */}
            {recordsIndividuales.masHatTricks && recordsIndividuales.masHatTricks.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-800">Mayor número de hat-tricks (3 goles)</span>
                  <Footprints className="h-5 w-5 text-green-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.masHatTricks.map((r, ridx) => (
                    <div key={ridx}>
                      <p className="text-xl font-bold text-green-900">{r.nombre}</p>
                      <p className="text-sm text-green-800">{r.cantidad} hat-tricks</p>
                      {r.partidos && r.partidos.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-green-700 mb-1">Partidos:</p>
                          <div className="space-y-1">
                            {r.partidos.map((partido, idx) => (
                              <div key={idx} className="text-xs text-green-800 flex items-center gap-2">
                                <span>Temporada {partido.año} - vs {partido.rival}</span>
                                <Link
                                  to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Ver partido
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Más Pokers */}
            {recordsIndividuales.masPokers && recordsIndividuales.masPokers.length > 0 && (
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-purple-800">Mayor número de pokers (4 goles)</span>
                  <Footprints className="h-5 w-5 text-purple-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.masPokers.map((r, ridx) => (
                    <div key={ridx}>
                      <p className="text-xl font-bold text-purple-900">{r.nombre}</p>
                      <p className="text-sm text-purple-800">{r.cantidad} pokers</p>
                      {r.partidos && r.partidos.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-purple-700 mb-1">Partidos:</p>
                          <div className="space-y-1">
                            {r.partidos.map((partido, idx) => (
                              <div key={idx} className="text-xs text-purple-800 flex items-center gap-2">
                                <span>Temporada {partido.año} - vs {partido.rival}</span>
                                <Link
                                  to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Ver partido
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Jugador con más partidos seguidos marcando */}
            {recordsIndividuales.jugadorMasPartidosSeguidosMarcando && recordsIndividuales.jugadorMasPartidosSeguidosMarcando.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-amber-800">Jugador con más partidos seguidos marcando</span>
                    <Footprints className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {recordsIndividuales.jugadorMasPartidosSeguidosMarcando.map((r, ridx) => (
                      <div key={ridx}>
                        <p className="text-xl font-bold text-amber-900">{r.nombre}</p>
                        <p className="text-sm text-amber-800">{r.partidosSeguidosMarcando} partidos seguidos</p>
                        {r.partidos && r.partidos.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-amber-700 mb-1">Partidos:</p>
                            <div className="space-y-1">
                              {r.partidos.map((partido, idx) => (
                                <div key={idx} className="text-xs text-amber-800 flex items-center gap-2">
                                  <span>Temporada {partido.año} - vs {partido.rival}</span>
                                  <span className="text-green-600 font-medium">({partido.marcador || '-'})</span>
                                  <Link
                                    to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    Ver partido
                                  </Link>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
            )}

            {/* Mejor ratio de goles por partido */}
            {recordsIndividuales.mejorRatioGoles && (
              <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-emerald-800">Mejor ratio de goles por partido (min 10 partidos)</span>
                  <Footprints className="h-5 w-5 text-emerald-600" />
                </div>
                <p className="text-2xl font-bold text-emerald-900">{recordsIndividuales.mejorRatioGoles.nombre}</p>
                <p className="text-lg text-emerald-800">{recordsIndividuales.mejorRatioGoles.golesTotales} goles en {recordsIndividuales.mejorRatioGoles.partidosJugados} partidos</p>
                <p className="text-sm text-emerald-700 mt-2">
                  Ratio: {recordsIndividuales.mejorRatioGoles.ratio?.toFixed(2)} goles/partido
                </p>
              </div>
            )}

            {/* Mejor ratio de asistencias por partido */}
            {recordsIndividuales.mejorRatioAsistencias && (
              <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-lg p-4 border border-teal-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-teal-800">Mejor ratio de asistencias por partido (min 10 partidos)</span>
                  <Award className="h-5 w-5 text-teal-600" />
                </div>
                <p className="text-2xl font-bold text-teal-900">{recordsIndividuales.mejorRatioAsistencias.nombre}</p>
                <p className="text-lg text-teal-800">{recordsIndividuales.mejorRatioAsistencias.asistencias} asistencias en {recordsIndividuales.mejorRatioAsistencias.partidosJugados} partidos</p>
                <p className="text-sm text-teal-700 mt-2">
                  Ratio: {recordsIndividuales.mejorRatioAsistencias.ratio?.toFixed(2)} asistencias/partido
                </p>
              </div>
            )}

            {/* Mejor ratio de amonestaciones por partido */}
            {recordsIndividuales.mejorRatioAmonestaciones && (
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-orange-800">Mayor ratio de amonestaciones por partido (min 10 partidos)</span>
                  <Shield className="h-5 w-5 text-orange-600" />
                </div>
                <p className="text-2xl font-bold text-orange-900">{recordsIndividuales.mejorRatioAmonestaciones.nombre}</p>
                <p className="text-lg text-orange-800">{recordsIndividuales.mejorRatioAmonestaciones.amonestaciones} amonestaciones en {recordsIndividuales.mejorRatioAmonestaciones.partidosJugados} partidos</p>
                <p className="text-sm text-orange-700 mt-2">
                  Ratio: {recordsIndividuales.mejorRatioAmonestaciones.ratio?.toFixed(2)} amonestaciones/partido
                </p>
              </div>
            )}

            {/* Portero con más partidos */}
            {recordsIndividuales.porteroMasPartidos && recordsIndividuales.porteroMasPartidos.length > 0 && (
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-800">Portero con más partidos jugados</span>
                  <Shield className="h-5 w-5 text-slate-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.porteroMasPartidos.map((r, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold text-slate-900">{r.nombre}</p>
                      <p className="text-sm text-slate-800">{r.partidosJugados} partidos</p>
                      <p className="text-xs text-slate-700 mt-1">Goles encajados: {r.golesEncajados} ({r.ratio?.toFixed(2)} por partido)</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portero con menos goles encajados */}
            {recordsIndividuales.porteroMenosGolesEncajados && recordsIndividuales.porteroMenosGolesEncajados.length > 0 && (
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-indigo-800">Portero con mejor ratio goles encajados (min 10 partidos)</span>
                  <Shield className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.porteroMenosGolesEncajados.map((r, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold text-indigo-900">{r.nombre}</p>
                      <p className="text-sm text-indigo-800">{r.golesEncajados} goles en {r.partidosJugados} partidos</p>
                      <p className="text-xs text-indigo-700 mt-1">Ratio: {r.ratio?.toFixed(2)} goles/partido</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portero con más partidos sin encajar */}
            {recordsIndividuales.porteroMasPartidosSinEncajar && recordsIndividuales.porteroMasPartidosSinEncajar.length > 0 && (
              <div className="bg-gradient-to-r from-lime-50 to-lime-100 rounded-lg p-4 border border-lime-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-lime-800">Portero con más partidos sin encajar gol</span>
                  <Shield className="h-5 w-5 text-lime-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.porteroMasPartidosSinEncajar.map((r, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold text-lime-900">{r.nombre}</p>
                      <p className="text-sm text-lime-800">{r.partidosSinEncajar} partidos sin encajar</p>
                      {r.partidos && r.partidos.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-lime-700 mb-1">Partidos:</p>
                          <div className="space-y-1">
                            {r.partidos.map((partido, idx) => (
                              <div key={idx} className="text-xs text-lime-800 flex items-center gap-2">
                                <span>Temporada {partido.año} - vs {partido.rival}</span>
                                <span className="text-green-600 font-medium">({partido.marcador || '-'})</span>
                                <Link
                                  to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Ver partido
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portero con más partidos seguidos sin encajar */}
            {recordsIndividuales.porteroMasPartidosSeguidosSinEncajar && recordsIndividuales.porteroMasPartidosSeguidosSinEncajar.length > 0 && (
              <div className="bg-gradient-to-r from-rose-50 to-rose-100 rounded-lg p-4 border border-rose-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-rose-800">Portero con más partidos seguidos sin encajar gol</span>
                  <Shield className="h-5 w-5 text-rose-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recordsIndividuales.porteroMasPartidosSeguidosSinEncajar.map((r, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold text-rose-900">{r.nombre}</p>
                      <p className="text-sm text-rose-800">{r.partidosSeguidosSinEncajar} partidos seguidos</p>
                      {r.partidos && r.partidos.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-rose-700 mb-1">Partidos:</p>
                          <div className="space-y-1">
                            {r.partidos.map((partido, idx) => (
                              <div key={idx} className="text-xs text-rose-800 flex items-center gap-2">
                                <span>Temporada {partido.año} - vs {partido.rival}</span>
                                <span className="text-green-600 font-medium">({partido.marcador || '-'})</span>
                                <Link
                                  to={`/historico/${partido.temporadaId}/${partido.partidoId}`}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Ver partido
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!((recordsIndividuales.masPremiosPichichi && recordsIndividuales.masPremiosPichichi.length > 0) ||
              (recordsIndividuales.masPremiosAsistente && recordsIndividuales.masPremiosAsistente.length > 0) ||
              (recordsIndividuales.masPremiosAmonestado && recordsIndividuales.masPremiosAmonestado.length > 0) ||
              (recordsIndividuales.masHatTricks && recordsIndividuales.masHatTricks.length > 0) ||
              (recordsIndividuales.masPokers && recordsIndividuales.masPokers.length > 0) ||
              recordsIndividuales.mejorRatioGoles ||
              recordsIndividuales.mejorRatioAsistencias ||
              recordsIndividuales.mejorRatioAmonestaciones ||
              (recordsIndividuales.porteroMasPartidos && recordsIndividuales.porteroMasPartidos.length > 0) ||
              (recordsIndividuales.porteroMenosGolesEncajados && recordsIndividuales.porteroMenosGolesEncajados.length > 0) ||
              (recordsIndividuales.porteroMasPartidosSinEncajar && recordsIndividuales.porteroMasPartidosSinEncajar.length > 0) ||
              (recordsIndividuales.porteroMasPartidosSeguidosSinEncajar && recordsIndividuales.porteroMasPartidosSeguidosSinEncajar.length > 0) ||
              (recordsIndividuales.jugadorMasPartidosSeguidosMarcando && recordsIndividuales.jugadorMasPartidosSeguidosMarcando.length > 0)
            ) && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600">No hay records individuales registrados.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderNuevoPartidoForm = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Nuevo Partido</h2>
        <button
          type="button"
          onClick={() => setRegistrarNuevoPartido(false)}
          className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
        >
          Volver a Registrar Partido
        </button>
      </div>
      
      <form onSubmit={handleSubmitPartido} className="space-y-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Datos del Partido</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Temporada</label>
              <select
                name="temporadaId"
                value={partidoForm.temporadaId}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Seleccionar temporada</option>
                {temporadas.map(t => (
                  <option key={t.id} value={t.id}>{t.año}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
              <input
                type="date"
                name="fecha"
                value={partidoForm.fecha}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lugar</label>
              <input
                type="text"
                name="lugar"
                value={partidoForm.lugar}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rival</label>
              <input
                type="text"
                name="rival"
                value={partidoForm.rival}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resultado (Ganado/Empate/Derrota)</label>
              <select
                name="resultado"
                value={partidoForm.resultado}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar</option>
                <option value="Ganado">Ganado</option>
                <option value="Empate">Empate</option>
                <option value="Derrota">Derrota</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marcador (ej: 2-1)</label>
              <input
                type="text"
                name="marcador"
                value={partidoForm.marcador}
                onChange={handlePartidoChange}
                placeholder="Ej: 2-1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número de Jornada</label>
              <input
                type="number"
                name="numeroJornada"
                value={partidoForm.numeroJornada}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goles 1ª Parte</label>
              <input
                type="number"
                name="golesPrimeraParte"
                value={partidoForm.golesPrimeraParte}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goles 2ª Parte</label>
              <input
                type="number"
                name="golesSegundaParte"
                value={partidoForm.golesSegundaParte}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goles Rival 1ª Parte</label>
              <input
                type="number"
                name="golesRivalPrimeraParte"
                value={partidoForm.golesRivalPrimeraParte}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goles Rival 2ª Parte</label>
              <input
                type="number"
                name="golesRivalSegundaParte"
                value={partidoForm.golesRivalSegundaParte}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ida/Vuelta</label>
              <select
                name="idaVuelta"
                value={partidoForm.idaVuelta}
                onChange={handlePartidoChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar</option>
                <option value="IDA">Ida</option>
                <option value="VUELTA">Vuelta</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">No Presentado Rival</label>
              <input
                type="checkbox"
                name="noPresentadoRival"
                checked={partidoForm.noPresentadoRival}
                onChange={(e) => setPartidoForm({ ...partidoForm, noPresentadoRival: e.target.checked })}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">No Presentado Nosotros</label>
              <input
                type="checkbox"
                name="noPresentadoNosotros"
                checked={partidoForm.noPresentadoNosotros}
                onChange={(e) => setPartidoForm({ ...partidoForm, noPresentadoNosotros: e.target.checked })}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              name="descripcion"
              value={partidoForm.descripcion}
              onChange={handlePartidoChange}
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Jugadores que jugaron el partido</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar Jugadores (múltiple)</label>
            <select
              multiple
              size={8}
              value={jugadoresSeleccionados.map(j => String(j.id))}
              onChange={handleJugadoresSeleccionadosChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {jugadores.map(j => (
                <option key={j.id} value={j.id}>{j.nombre} ({j.posicion || 'Sin posición'})</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Mantén presionada Ctrl/Cmd para seleccionar múltiples jugadores</p>
          </div>

          {jugadoresSeleccionados.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Jugadores seleccionados ({jugadoresSeleccionados.length}):</h4>
              <div className="flex flex-wrap gap-2">
                {jugadoresSeleccionados.map(j => (
                  <div
                    key={j.id}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                  >
                    {j.nombre} ({j.posicion || '-'})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Estadísticas de Jugadores</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jugador</label>
              <select
                value={golForm.jugadorId}
                onChange={handleGolFormChange}
                name="jugadorId"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar jugador de la lista</option>
                {jugadoresSeleccionados.map(j => (
                  <option key={j.id} value={j.id}>{j.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minutos Jugados</label>
              <input
                type="number"
                name="minutosJugados"
                value={golForm.minutosJugados}
                onChange={handleGolFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asistencias</label>
              <input
                type="number"
                name="asistencias"
                value={golForm.asistencias}
                onChange={handleGolFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tarjetas Amarillas</label>
              <input
                type="number"
                name="tarjetasAmarillas"
                value={golForm.tarjetasAmarillas}
                onChange={handleGolFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tarjetas Rojas</label>
              <input
                type="number"
                name="tarjetasRojas"
                value={golForm.tarjetasRojas}
                onChange={handleGolFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tarjetas Azules</label>
              <input
                type="number"
                name="tarjetasAzules"
                value={golForm.tarjetasAzules}
                onChange={handleGolFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={agregarEstadisticasJugador}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Agregar Estadísticas
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Goleadores</h3>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Jugador</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {jugadoresSeleccionados.map(j => (
                <label key={j.id} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="nuevoGolJugadorId"
                    value={String(j.id)}
                    checked={registrarGolForm.jugadorId === String(j.id)}
                    onChange={() => setRegistrarGolForm({ ...registrarGolForm, jugadorId: String(j.id) })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{j.nombre}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de gol</label>
              <select
                name="tipoDeGol"
                value={registrarGolForm.tipoDeGol}
                onChange={handleRegistrarGolFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar</option>
                <option value="PIE_DERECHO">Pie derecho</option>
                <option value="PIE_IZQUIERDO">Pie izquierdo</option>
                <option value="CABEZA">Cabeza</option>
                <option value="BALON_PARADO">Balón parado</option>
                <option value="NO_ESPECIFICADO">No especificado</option>
                <option value="PROPIA_META">Propia meta</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Parte</label>
              <div className="space-y-2">
                {[
                  { value: '', label: 'No especificado' },
                  { value: '1', label: '1ª parte' },
                  { value: '2', label: '2ª parte' }
                ].map(opcion => (
                  <label key={opcion.value || 'sin-parte'} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="parte"
                      value={opcion.value}
                      checked={registrarGolForm.parte === opcion.value}
                      onChange={handleRegistrarGolFormChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{opcion.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Resultado tras gol</label>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-2">Nuestro equipo</p>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                    <label key={`nuevo-local-${num}`} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="resultadoTrasGolLocal"
                        value={String(num)}
                        checked={registrarGolForm.resultadoTrasGolLocal === String(num)}
                        onChange={handleRegistrarGolFormChange}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 w-5 text-center font-semibold">{num}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-red-700 mb-2">Equipo rival</p>
                <div className="flex flex-wrap gap-2">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                    <label key={`nuevo-visitante-${num}`} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="resultadoTrasGolVisitante"
                        value={String(num)}
                        checked={registrarGolForm.resultadoTrasGolVisitante === String(num)}
                        onChange={handleRegistrarGolFormChange}
                        className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700 w-5 text-center font-semibold">{num}</span>
                    </label>
                  ))}
                </div>
              </div>
              <input
                type="text"
                name="resultadoTrasGol"
                placeholder="Ej: 5-2, 10-0 (para resultados personalizados)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                value={registrarGolForm.resultadoTrasGol}
                onChange={handleRegistrarGolFormChange}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={agregarGolNuevoPartido}
            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
          >
            Agregar gol
          </button>

          {partidoForm.goles.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jugador</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resultado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parte</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {partidoForm.goles.map((gol, index) => {
                    const jugador = jugadoresSeleccionados.find(j => j.id === gol.jugadorId)
                      || jugadores.find(j => j.id === gol.jugadorId);
                    return (
                      <tr key={index}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{jugador?.nombre || 'Desconocido'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gol.tipoDeGol}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gol.resultadoTrasGol || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{gol.parte || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            type="button"
                            onClick={() => eliminarGolNuevoPartido(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {partidoForm.jugadoresParticipantes.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Estadísticas Registradas</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Jugador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Minutos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Asistencias</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Amarillas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Rojas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Azules</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {partidoForm.jugadoresParticipantes.map((jp, index) => {
                  const jugador = jugadoresSeleccionados.find(j => j.id === jp.jugadorId);
                  return (
                    <tr key={index}>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{jugador?.nombre || 'Desconocido'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.minutosJugados || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.asistencias || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.tarjetasAmarillas || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.tarjetasRojas || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{jp.tarjetasAzules || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          type="button"
                          onClick={() => eliminarJugador(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-semibold"
        >
          {loading ? 'Guardando...' : 'Guardar Partido'}
        </button>
      </form>
    </div>
  );

  // Sync activeTab with URL and handle URL parameters
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/temporadas')) {
      setActiveTab('temporadas');
    } else if (path.startsWith('/historico')) {
      setActiveTab('historico');
      if (temporadas.length === 0) {
        fetchTemporadas();
      }
      if (temporadaId) {
        const temporada = temporadas.find(t => String(t.id) === String(temporadaId));
        if (temporada) {
          if (String(temporadaSeleccionada?.id) !== String(temporadaId)) {
            setTemporadaSeleccionada(temporada);
            setPartidoSeleccionado(null);
            fetchPartidosByTemporada(temporadaId);
          }
        }
      } else {
        setTemporadaSeleccionada(null);
        setPartidosTemporada([]);
        setPartidoSeleccionado(null);
      }
      if (partidoId && temporadaId && String(partidoSeleccionado?.id) !== String(partidoId)) {
        handlePartidoClick({ id: parseInt(partidoId) });
      } else if (!partidoId && partidoSeleccionado) {
        setPartidoSeleccionado(null);
      }
    } else if (path === '/all-mnls') {
      setActiveTab('allMnls');
    } else if (path === '/busqueda-partidos') {
      setActiveTab('busquedaPartidos');
    } else if (path.startsWith('/jugadores')) {
      setActiveTab('detalleJugador');
      if (jugadorId && jugadorEstadisticasId !== jugadorId) {
        setJugadorEstadisticasId(jugadorId);
        fetchEstadisticasJugador(jugadorId);
        fetchRecordsJugador(jugadorId);
      }
    } else if (path === '/registrar-partido') {
      setActiveTab('registrarPartido');
    } else if (path === '/records') {
      setActiveTab('records');
    } else {
      setActiveTab('allMnls');
    }
  }, [location.pathname, temporadaId, partidoId, jugadorId, temporadas, temporadaSeleccionada, partidoSeleccionado, jugadorEstadisticasId]);

  useEffect(() => {
    if (location.pathname === '/registrar-partido') {
      if (registrarInitPath.current !== location.pathname) {
        registrarInitPath.current = location.pathname;
        initRegistrarPartidoRef.current();
      }
    } else {
      registrarInitPath.current = '';
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/busqueda-partidos') {
      if (busquedaInitPath.current !== location.pathname) {
        busquedaInitPath.current = location.pathname;
        setRivalBusqueda('');
        cargarPartidosBusqueda();
      }
    } else {
      busquedaInitPath.current = '';
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen">
      <Navbar
        activeTab={activeTab}
        onTemporadasClick={() => initTemporadaVista()}
        onHistoricoClick={() => {
          fetchTemporadas();
          setTemporadaSeleccionada(null);
          setPartidosTemporada([]);
          setPartidoSeleccionado(null);
        }}
        onAllMnlsClick={() => fetchEstadisticasAgregadas()}
        onJugadoresClick={() => fetchJugadores()}
        onRecordsClick={() => {
          fetchRecordsEquipo();
          fetchRecordsIndividuales();
        }}
      />

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
        {activeTab === 'temporadas' && renderTemporadaVistaTab()}
        {activeTab === 'historico' && renderHistoricoTab()}
        {activeTab === 'allMnls' && renderAllMnlsTab()}
        {activeTab === 'busquedaPartidos' && renderBusquedaPartidosTab()}
        {activeTab === 'detalleJugador' && renderDetalleJugadorTab()}
        {activeTab === 'registrarPartido' && (
          <ProtectedRoute>
            {renderRegistrarPartidoTab()}
          </ProtectedRoute>
        )}
        {activeTab === 'records' && renderRecordsTab()}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/temporadas" element={<AppContent />} />
          <Route path="/historico" element={<AppContent />} />
          <Route path="/historico/:temporadaId" element={<AppContent />} />
          <Route path="/historico/:temporadaId/:partidoId" element={<AppContent />} />
          <Route path="/all-mnls" element={<AppContent />} />
          <Route path="/busqueda-partidos" element={<AppContent />} />
          <Route path="/jugadores" element={<AppContent />} />
          <Route path="/jugadores/:jugadorId" element={<AppContent />} />
          <Route path="/registrar-partido" element={<AppContent />} />
          <Route path="/admin" element={<Navigate to="/registrar-partido" replace />} />
          <Route path="/records" element={<AppContent />} />
          <Route path="/login" element={<Login />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
